import React, { useState } from "react";

import { CATEGORIES_AFFICHAGE, COULEURS_CATEGORIES } from "./categoriesFlux";
import { BulleFlottante, ContenuBulleEnv, useBulle } from "./BulleFlottante";
import { STATUTS_META, agregerGroupes, statutCategorie } from "./filtresMap";

const R = 13; // rayon d'un hexagone
const LARGEUR = Math.sqrt(3) * R;
const PAS_VERTICAL = 1.5 * R;
const COULEUR_HORS_LENTILLE = "#eef1f5";

// Sommets d'un hexagone "pointe en haut" centre en (cx, cy) : "x,y x,y ...".
export function pointsHexagone(cx, cy, r = R) {
  return [0, 1, 2, 3, 4, 5]
    .map((i) => {
      const a = (Math.PI / 180) * (60 * i - 30);
      return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
    })
    .join(" ");
}

// Etat d'un environnement sous la lentille : null (hors lentille), ou { statut, running }.
export function etatLentille(env, lentille) {
  if (lentille === "ALL") return { statut: env.statut, running: env.running };
  const e = statutCategorie(env, lentille);
  return e ? { statut: e.statut, running: e.running } : null;
}

// Disposition d'un groupe : colonnes / lignes / dimensions du SVG.
export function dispositionGroupe(n) {
  const cols = Math.max(3, Math.min(12, Math.ceil(Math.sqrt(n * 1.6))));
  const lignes = Math.ceil(n / cols);
  return {
    cols,
    lignes,
    largeur: cols * LARGEUR + LARGEUR / 2 + 4,
    hauteur: lignes * PAS_VERTICAL + R / 2 + 4,
  };
}

// Carte hexagonale : un hexagone par environnement, regroupes par groupe ; la "lentille"
// recolore selon une seule categorie. onEnv(env, categorieOuNull).
export default function VueHexagones({ envs, onEnv }) {
  const [lentille, setLentille] = useState("ALL");
  const { bulle, montrer, cacher } = useBulle();
  const groupes = agregerGroupes(envs);
  return (
    <div data-testid="vue-hex">
      <div className="map-lens" role="group" aria-label="Lentille">
        Lentille :
        {["ALL", ...CATEGORIES_AFFICHAGE].map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={lentille === k}
            className={`map-chip${lentille === k ? " on" : ""}`}
            style={{ "--cc": COULEURS_CATEGORIES[k] || "var(--m-navy)" }}
            onClick={() => setLentille(k)}
          >
            {COULEURS_CATEGORIES[k] && (
              <span className="map-dot" style={{ background: COULEURS_CATEGORIES[k] }} />
            )}
            {k === "ALL" ? "Tous les flux" : k}
          </button>
        ))}
      </div>
      {!groupes.length ? (
        <div className="map-vide">Aucun traitement ne correspond aux filtres</div>
      ) : (
        <div className="map-hexwrap">
          {groupes.map((g) => {
            const d = dispositionGroupe(g.envs.length);
            return (
              <div key={g.group} className="map-hexgrp">
                <div className="map-hexgrp-t" title={g.group}>
                  <span className="map-dot" style={{ background: STATUTS_META[g.statut].couleur }} />
                  {g.group}
                  {g.erreurs ? (
                    <span style={{ color: "#e5484d", fontWeight: 500 }}> · {g.erreurs}</span>
                  ) : null}
                </div>
                <svg width={d.largeur} height={d.hauteur}>
                  <defs>
                    <pattern
                      id="map-hachures-nr-hex"
                      width="4"
                      height="4"
                      patternUnits="userSpaceOnUse"
                      patternTransform="rotate(45)"
                    >
                      <rect width="1" height="4" fill="rgba(255,255,255,.35)" />
                    </pattern>
                  </defs>
                  {g.envs.map((e, i) => {
                    const r = Math.floor(i / d.cols);
                    const c = i % d.cols;
                    const cx = LARGEUR / 2 + 2 + c * LARGEUR + (r % 2 ? LARGEUR / 2 : 0);
                    const cy = R + 2 + r * PAS_VERTICAL;
                    const etat = etatLentille(e, lentille);
                    const nr = !!etat && etat.statut === "non_remonte";
                    return (
                      <React.Fragment key={e.key}>
                      <polygon
                        points={pointsHexagone(cx, cy)}
                        fill={etat ? STATUTS_META[etat.statut].couleur : COULEUR_HORS_LENTILLE}
                        stroke="#fff"
                        strokeWidth="1.5"
                        className={etat && etat.running ? "running" : undefined}
                        style={{ cursor: etat ? "pointer" : "default" }}
                        data-testid={`hex-${e.key}`}
                        onMouseMove={(ev) => montrer(ev, <ContenuBulleEnv env={e} />)}
                        onMouseLeave={cacher}
                        onClick={() => {
                          if (!etat) return;
                          cacher();
                          onEnv(e, lentille === "ALL" ? null : lentille);
                        }}
                      />
                      {nr && (
                        <polygon
                          points={pointsHexagone(cx, cy)}
                          fill="url(#map-hachures-nr-hex)"
                          style={{ pointerEvents: "none" }}
                        />
                      )}
                      </React.Fragment>
                    );
                  })}
                </svg>
              </div>
            );
          })}
        </div>
      )}
      <BulleFlottante bulle={bulle} />
    </div>
  );
}
